import { SubscriptionView } from '@/types'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface SubscriptionUIState {
  // UI state
  currentView: SubscriptionView
  isHistoryExpanded: boolean
  showCancellationModal: boolean

  // Preferences
  dateFormat: 'HU' | 'EU' | 'US'

  // Actions
  setCurrentView: (view: SubscriptionView) => void
  toggleHistoryView: () => void
  openCancellationModal: () => void
  closeCancellationModal: () => void
  setDateFormat: (format: 'HU' | 'EU' | 'US') => void

  reset(): void
}

const initialState = {
  currentView: 'current' as SubscriptionView,
  isHistoryExpanded: false,
  showCancellationModal: false,
  dateFormat: 'HU' as const,
}

export const useSubscriptionStore = create<SubscriptionUIState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setCurrentView: (view: SubscriptionView) =>
          set(
            {
              currentView: view,
            },
            false,
            'subscription/setCurrentView'
          ),

        toggleHistoryView: () =>
          set(
            (state) => ({ isHistoryExpanded: !state.isHistoryExpanded }),
            false,
            'subscription/toggleHistory'
          ),

        openCancellationModal: () =>
          set(
            { showCancellationModal: true },
            false,
            'subscription/openCancellationModal'
          ),

        closeCancellationModal: () =>
          set(
            { showCancellationModal: false },
            false,
            'subscription/closeCancellationModal'
          ),

        setDateFormat: (format: 'HU' | 'EU' | 'US') =>
          set({ dateFormat: format }, false, 'subscription/setDateFormat'),

        reset: () => set(() => ({ ...initialState })),
      }),
      {
        name: 'subscription-store',
        partialize: (state: SubscriptionUIState) => ({
          currentView: state.currentView,
          isHistoryExpanded: state.isHistoryExpanded,
          showCancellationModal: state.showCancellationModal,
        }),
      }
    ),
    { name: 'SubscriptionStore' }
  )
)

// Selector hooks for optimal re-renders
export const useSubscriptionView = () =>
  useSubscriptionStore((state) => state.currentView)
